import {
  Table,
  TableHead,
  TableRow,
  TableBody,
  Checkbox,
  Box,
  Typography,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { styled } from '@mui/material/styles';
import { GeneralTableBox, HeadTableCell, RowTableCell, TableBodySkeleton } from '../../../common/components';
import { formatCurrency, formatDate } from '../../../utils/formatUtils';
import {
  buildRowKey,
  formatCouponLabel,
  getSubscriberEmailSummary,
  getSubscriberNameSummary,
} from '../utils/debtDetailUtils';
import { useState, memo } from 'react';

const ExpandButton = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
  padding: theme.spacing(0.5),
  cursor: 'pointer',
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const CouponRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.background.default,
  },
}));

const DebtDetailRow = memo(
  ({ item, onToggleCoupon, onToggleRow, isCouponSelected, isRowFullySelected }) => {
    const [expanded, setExpanded] = useState(false);
    const header = item.header || {};
    const coupons = item.coupons || [];
    const rowKey = buildRowKey(header);
    const allSelected = isRowFullySelected(header, coupons);
    const subscriberSummary = getSubscriberNameSummary(header);
    const emailSummary = getSubscriberEmailSummary(header);

    const handleToggleRow = () => {
      onToggleRow(header, coupons);
    };

    return (
      <>
        <TableRow hover selected={allSelected}>
          <RowTableCell>
            <Checkbox checked={allSelected} onChange={handleToggleRow} color="primary" />
          </RowTableCell>
          <RowTableCell>
            <ExpandButton onClick={() => setExpanded((prev) => !prev)}>
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </ExpandButton>
          </RowTableCell>
          <RowTableCell>
            <Typography variant="subtitle2">{header.contract_number}</Typography>
          </RowTableCell>
          <RowTableCell>
            <Typography variant="subtitle1" fontWeight={600}>
              {subscriberSummary}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {emailSummary}
            </Typography>
          </RowTableCell>
          <RowTableCell>{header.is_possessor ? 'Sí' : 'No'}</RowTableCell>
          <RowTableCell>{header.overdue_installments ?? coupons.length}</RowTableCell>
          <RowTableCell>{formatCurrency(header.total)}</RowTableCell>
          <RowTableCell>{formatCurrency(header.anniversary_overdue_debt || 0)}</RowTableCell>
          <RowTableCell>
            {formatCurrency(header.anniversary_pending_amount || 0)}
            {header.anniversary_missing_agreement_date ? (
              <Typography variant="caption" color="error" display="block">
                Falta acuerdo
              </Typography>
            ) : null}
          </RowTableCell>
          <RowTableCell>{formatCurrency(header.combined_debt_amount ?? header.total)}</RowTableCell>
        </TableRow>
        <TableRow>
          <RowTableCell colSpan={10}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <GeneralTableBox hasMaxHeight={false} sx={{ justifyContent: 'center', px: 12, py: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <HeadTableCell padding="checkbox" />
                      <HeadTableCell>Cuota</HeadTableCell>
                      <HeadTableCell>Vencimiento</HeadTableCell>
                      <HeadTableCell>Importe</HeadTableCell>
                      <HeadTableCell>Tipo</HeadTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {coupons.map((coupon) => {
                      const feeLabel = formatCouponLabel(coupon);
                      const dueDate = coupon.third_due_date || coupon.regulariza1_date;
                      const amount = coupon.third_due_amount ?? coupon.regulariza1_amount ?? 0;
                      const selected = isCouponSelected(header, coupon.uuid);
                      const feeNumberRaw = coupon.fee_number1;
                      const feeNumberValue =
                        feeNumberRaw === null || feeNumberRaw === undefined
                          ? null
                          : Number(feeNumberRaw);
                      const isAdequacy =
                        coupon.plan_regulariza || (feeNumberValue !== null && !Number.isNaN(feeNumberValue) && feeNumberValue === 0);

                      return (
                        <CouponRow key={`${rowKey}-${coupon.uuid}`}>
                          <RowTableCell padding="checkbox">
                            <Checkbox
                              checked={selected}
                              onChange={() => onToggleCoupon(header, coupon)}
                              color="primary"
                            />
                          </RowTableCell>
                          <RowTableCell>{feeLabel}</RowTableCell>
                          <RowTableCell>{dueDate ? formatDate(dueDate) : '-'}</RowTableCell>
                          <RowTableCell>{formatCurrency(amount)}</RowTableCell>
                          <RowTableCell>
                            {isAdequacy ? 'Adecuación' : 'Cuota regular'}
                          </RowTableCell>
                        </CouponRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </GeneralTableBox>
            </Collapse>
          </RowTableCell>
        </TableRow>
      </>
    );
  }
);

const DebtDetailDataTable = ({
  data,
  isLoading,
  onToggleCoupon,
  onToggleRow,
  isCouponSelected,
  isRowFullySelected,
}) => {
  return (
    <GeneralTableBox>
      <Table size="small">
        <TableHead>
          <TableRow>
            <HeadTableCell padding="checkbox" />
            <HeadTableCell />
            <HeadTableCell>Contrato</HeadTableCell>
            <HeadTableCell>Suscriptor</HeadTableCell>
            <HeadTableCell>Poseedor</HeadTableCell>
            <HeadTableCell>Cupones vencidos</HeadTableCell>
            <HeadTableCell>Monto adeudado</HeadTableCell>
            <HeadTableCell>Aniv. mora</HeadTableCell>
            <HeadTableCell>Aniv. saldo</HeadTableCell>
            <HeadTableCell>Deuda combinada</HeadTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableBodySkeleton />
          ) : !data.length ? (
            <TableRow>
              <RowTableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}>
                No se encontraron registros que coincidan con los filtros seleccionados.
              </RowTableCell>
            </TableRow>
          ) : (
            data.map((item) => (
              <DebtDetailRow
                key={buildRowKey(item.header)}
                item={item}
                onToggleCoupon={onToggleCoupon}
                onToggleRow={onToggleRow}
                isCouponSelected={isCouponSelected}
                isRowFullySelected={isRowFullySelected}
              />
            ))
          )}
        </TableBody>
      </Table>
    </GeneralTableBox>
  );
};

export default DebtDetailDataTable;
